import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FileText, Book, Download, Trash2, Edit, Plus, Loader2, ArrowLeft, Scale, TrendingUp, CheckCircle, Briefcase, Globe, Mail, User, Award, FileBarChart, History, Languages, Shield, Copy, Eye, BarChart, File, Info, List, Monitor, Star, Target, X, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { API, BACKEND_URL } from '../utils/constants';
import { subirAlDossier, errorDeSubida } from '../utils/subirAlDossier';

// NIW Section titles (shared)
const NIW_SECTION_TITLES = [
  "I. Cover Page",
  "II. Executive Summary",
  "III. Statement of Substantial Merit & National Importance (Prong 1)",
  "IV. Problem & National Context",
  "IV-B. Petitioner's Qualifications & Demonstrated Capacity (Prong 2)",
  "V. Objectives",
  "VI. Indicators & Metrics",
  "VII. Scope & Deliverables",
  "VIII. Execution Plan by Phases",
  "IX. Capital-Free Start Strategy",
  "X. Methodology",
  "XI. Risk Management & Assumptions",
  "XII. Expected Results & Impact",
  "XIII. Governance, Ethics & Compliance",
  "XIV. Monitoring & Evaluation",
  "XV. Empirical Basis & References",
  "XVI. Annexes",
  "XVII. Balance of Factors & Waiver Justification (Prong 3)"
];

/**
 * ProjectActions — replace / paste / remove the case's active project.
 * Accepts Word and PDF: the text is extracted once here and shared by every module.
 */
const ProjectActions = ({ busy, hasProject, onUpload, onPaste, onRemove }) => {
  const inputId = 'case-project-file-input';
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <input
        id={inputId}
        type="file"
        accept=".docx,.pdf,.txt,.md"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          e.target.value = '';  // let the operator re-pick the same file after a failure
          onUpload(file);
        }}
      />
      <Button
        size="sm"
        variant={hasProject ? 'outline' : 'default'}
        disabled={busy}
        onClick={() => document.getElementById(inputId).click()}
      >
        {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <File className="mr-1" size={14} />}
        {hasProject ? 'Reemplazar proyecto (Word/PDF)' : 'Cargar proyecto (Word/PDF)'}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={onPaste}>
        <Edit className="mr-1" size={14} />
        Pegar texto
      </Button>
      {hasProject && (
        <Button size="sm" variant="outline" disabled={busy} onClick={onRemove} style={{ color: '#DC2626', borderColor: '#FECACA' }}>
          <Trash2 className="mr-1" size={14} />
          Quitar
        </Button>
      )}
    </div>
  );
};


/**
 * CaseDataCard — the client's CV + the ONE active project every deliverable uses.
 *
 * The CV and the project are captured AUTOMATICALLY from the last document generated
 * for this client (in ANY module — proposals, letters, etc.) and reused by every
 * generator, so the operator never re-attaches them and the documents can't disagree.
 *
 * The project is also REPLACEABLE here, which is the point of this card: when the
 * project changes (e.g. the client brings one written outside the platform), the
 * operator sets it once and it becomes PINNED — no generator's form can silently
 * restore the previous one, and the figures derived from the old project (name /
 * scale / geography / capital / Pro Forma) are reset so the next document re-derives
 * them from the new project.
 */
const CaseDataCard = ({ clientId }) => {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [pastedText, setPastedText] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/dossier?include_text=true`);
      setSummary(res.data || { has_dossier: false });
    } catch (e) {
      setSummary({ has_dossier: false });
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const hasCv = !!(summary && summary.has_cv);
  const hasProject = !!(summary && summary.has_project);
  const petitioner = (summary && summary.petitioner_name) || '';
  const projectText = (summary && (summary.project_text || summary.project_preview)) || '';
  const caseFacts = (summary && summary.case_facts) || null;
  const caseFinancials = (summary && summary.case_financials) || null;
  const isEmpty = !hasCv && !hasProject;
  const projectLocked = !!(summary && summary.project_locked);
  const projectSource = (summary && summary.project_source) || '';
  const projectFilename = (summary && summary.project_filename) || '';
  // Proyecto/CV guardados por una extracción vieja que decodificó el .docx como texto:
  // se ven como "f£<x)GEQR(|…". No sirven para redactar nada — hay que resubir el archivo.
  const projectIsBinary = !!(summary && summary.project_looks_binary);
  const cvIsBinary = !!(summary && summary.cv_looks_binary);

  // Replacing the project resets the pinned figures on purpose — they describe the
  // PREVIOUS project, so reusing them would keep the whole package anchored to it.
  const uploadProject = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('kind', 'project');
      form.append('file', file);
      const res = await axios.post(`${API}/clients/${clientId}/dossier/upload`, form);
      toast.success(`Proyecto actualizado (${(res.data?.chars || 0).toLocaleString()} caracteres). Los próximos documentos usarán este.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el proyecto.');
    } finally {
      setBusy(false);
    }
  };

  // El CV se guarda una vez en la ficha y todos los formularios lo precargan.
  const uploadCv = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('kind', 'cv');
      form.append('file', file);
      const res = await subirAlDossier(clientId, form);
      if (!res) return;   // era de otro cliente y el operador canceló
      toast.success(`CV guardado (${(res.data?.chars || 0).toLocaleString()} caracteres). Los formularios de este cliente lo precargan.`);
      await load();
    } catch (e) {
      toast.error(errorDeSubida(e, 'No se pudo cargar el CV.'));
    } finally {
      setBusy(false);
    }
  };

  const savePastedProject = async () => {
    const text = (pastedText || '').trim();
    if (text.length < 100) {
      toast.error('Pegá el proyecto completo (al menos 100 caracteres).');
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/clients/${clientId}/dossier`, { project_text: text });
      toast.success('Proyecto actualizado. Los próximos documentos usarán este.');
      setPasting(false);
      setPastedText('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el proyecto.');
    } finally {
      setBusy(false);
    }
  };

  const removeProject = async () => {
    if (!window.confirm('¿Quitar el proyecto activo del caso? Los documentos que generes después no tendrán proyecto hasta que cargues uno nuevo.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/clients/${clientId}/dossier/project`);
      toast.success('Proyecto quitado del caso.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo quitar el proyecto.');
    } finally {
      setBusy(false);
    }
  };

  const srcLabel = {
    business_plan: 'plan de negocios', patent: 'patente', book: 'libro',
    empresarial: 'proyecto empresarial',
    whitepaper: 'whitepaper', econometric_study: 'estudio econométrico',
    case_study: 'caso de estudio', policy_paper: 'policy paper',
    recommendation_letter: 'carta de recomendación', expert_letter: 'carta de experto',
    intent_letter: 'carta de intención', self_petition_letter: 'carta de autopetición',
    manual: 'carga manual',
  }[(summary && summary.source_doc_type)] || (summary && summary.source_doc_type) || '';

  const box = {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px',
    padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  };
  const chip = (ok) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px',
    borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
    background: ok ? '#DCFCE7' : '#FEF3C7', color: ok ? '#166534' : '#92400E',
    border: `1px solid ${ok ? '#86EFAC' : '#FCD34D'}`,
  });

  return (
    <div style={box}>
      <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#111827', marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Shield size={16} style={{ color: '#F8BF13' }} />
        Datos del caso
      </h3>
      <p style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '1rem' }}>
        El CV y el proyecto se guardan <strong>solos</strong>: se toman del último documento que generes para este
        cliente y se reutilizan en todos los demás. Si el proyecto cambió (por ejemplo, el cliente trae uno hecho
        fuera de la plataforma), <strong>reemplazalo acá</strong> y todos los entregables siguientes usarán ese.
      </p>

      {isEmpty ? (
        <div style={{ background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 8, padding: '0.9rem 1.1rem', fontSize: '0.85rem', color: '#6B7280' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            Todavía no hay datos de este cliente. Cargá el <strong>CV</strong> acá y todos los formularios lo
            precargan; el proyecto podés cargarlo ahora (Word/PDF) o dejar que se capture solo al generar el
            primer documento.
          </div>
          <label style={{ display: 'inline-block', marginBottom: '0.75rem', padding: '6px 12px', borderRadius: 8, background: '#1D4ED8', color: 'white', fontSize: '0.82rem', fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
            Subir CV (PDF o Word)
            <input
              type="file"
              accept=".pdf,.docx,.doc,.txt"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => { uploadCv(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          <ProjectActions
            busy={busy}
            hasProject={false}
            onUpload={uploadProject}
            onPaste={() => setPasting(true)}
            onRemove={removeProject}
          />
        </div>
      ) : (
        <>
          {petitioner && (
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#1E3A8A' }}>
              👤 Titular del caso (según el CV): <strong>{petitioner}</strong>
            </div>
          )}

          {/* CV */}
          <div style={{ paddingBottom: '1rem', borderBottom: '1px solid #F3F4F6', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem', marginBottom: 4 }}>Currículum (CV)</div>
            <span style={chip(hasCv && !cvIsBinary)}>
              {hasCv
                ? (cvIsBinary ? <>⚠️ Ilegible — resubilo</> : <><CheckCircle size={13} /> Cargado ({summary.cv_chars} car.)</>)
                : <>⚠️ Aún sin CV</>}
            </span>
            {/* Antes no había forma de cargar el CV desde la ficha: sólo llegaba
                como efecto de generar algún documento. Un cliente sin documentos
                previos abría cada formulario con el CV vacío (oct 2026). */}
            <label style={{ marginLeft: 10, fontSize: '0.8rem', color: '#1D4ED8', cursor: busy ? 'wait' : 'pointer', textDecoration: 'underline' }}>
              {hasCv && !cvIsBinary ? 'Reemplazar CV' : 'Subir CV (PDF o Word)'}
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                style={{ display: 'none' }}
                disabled={busy}
                onChange={(e) => { uploadCv(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>

          {/* Proyecto activo — reemplazable por la operadora */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>Proyecto activo</span>
              <span style={chip(hasProject && !projectIsBinary)}>
                {hasProject
                  ? (projectIsBinary ? <>⚠️ Ilegible</> : <><CheckCircle size={13} /> Cargado</>)
                  : <>⚠️ Aún sin proyecto</>}
              </span>
              {projectLocked && (
                <span style={{ ...chip(true), background: '#EFF6FF', color: '#1E40AF', borderColor: '#BFDBFE' }}
                      title="Cargado por la operadora: ningún generador lo sobreescribe con lo que traiga su formulario.">
                  📌 Fijado manualmente
                </span>
              )}
              {(projectFilename || srcLabel) && (
                <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  · origen: {projectFilename || srcLabel}
                </span>
              )}
            </div>
            {hasProject && projectIsBinary && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.7rem 0.9rem', fontSize: '0.82rem', color: '#991B1B', lineHeight: 1.5, marginBottom: '0.5rem' }}>
                <strong>Este proyecto no es texto legible.</strong> Se guardó cuando el sistema todavía no
                sabía leer Word, así que quedó el archivo crudo{projectFilename ? ` (${projectFilename})` : ''}.
                <strong> Volvé a subir el mismo archivo</strong> con el botón de abajo: ahora se lee bien.
                Mientras esté así, los documentos que generes lo ignoran (no se redacta con esto).
              </div>
            )}
            {hasProject && (
              <div style={{ maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '0.7rem 0.9rem', fontSize: '0.82rem', color: projectIsBinary ? '#9CA3AF' : '#374151', lineHeight: 1.5 }}>
                {projectText}
              </div>
            )}
            {hasProject && (
              <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.5rem 0 0.6rem' }}>
                Al reemplazarlo se descartan las cifras fijadas del proyecto anterior (titular, escala, geografía,
                capital y Pro Forma): el próximo documento las vuelve a derivar del proyecto nuevo.
              </p>
            )}
            <div style={{ marginTop: hasProject ? 0 : '0.6rem' }}>
              <ProjectActions
                busy={busy}
                hasProject={hasProject}
                onUpload={uploadProject}
                onPaste={() => setPasting(true)}
                onRemove={removeProject}
              />
            </div>
            {pasting && (
              <div style={{ marginTop: '0.75rem' }}>
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Pegá acá el proyecto completo…"
                  style={{ width: '100%', minHeight: 160, padding: '0.7rem 0.9rem', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: '0.82rem', fontFamily: 'inherit', lineHeight: 1.5 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button size="sm" onClick={savePastedProject} disabled={busy}>
                    {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle className="mr-1" size={14} />}
                    Guardar como proyecto activo
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setPasting(false); setPastedText(''); }} disabled={busy}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Cifras fijadas (solo lectura, pobladas tras el primer documento) */}
          {caseFacts && (caseFacts.petitioner_legal_name || caseFacts.scale_trajectory?.length > 0) && (
            <div style={{ marginTop: '0.5rem', background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8, padding: '0.9rem 1.1rem' }}>
              <div style={{ fontWeight: 700, color: '#166534', fontSize: '0.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={15} /> Datos fijados para todo el paquete
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem 1.5rem', fontSize: '0.82rem', color: '#14532D' }}>
                {caseFacts.petitioner_legal_name && <div><strong>Titular:</strong> {caseFacts.petitioner_legal_name}</div>}
                {caseFacts.current_employer && <div><strong>Empleador:</strong> {caseFacts.current_employer}</div>}
                {caseFacts.capital_initial && <div><strong>Capital inicial:</strong> {caseFacts.capital_initial}</div>}
                {caseFacts.geography_pilot && <div><strong>Piloto:</strong> {caseFacts.geography_pilot}{caseFacts.geography_expansion?.length ? ` → ${caseFacts.geography_expansion.join(', ')}` : ''}</div>}
                {caseFacts.scale_trajectory?.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>Escala{caseFacts.scale_unit ? ` (${caseFacts.scale_unit})` : ''}:</strong> {caseFacts.scale_trajectory.join(' → ')}{caseFacts.scale_cumulative_5yr ? ` · 5 años: ${caseFacts.scale_cumulative_5yr}` : ''}
                  </div>
                )}
              </div>
              {caseFinancials && caseFinancials.cumulative_revenue_5yr > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #BBF7D0', fontSize: '0.82rem', color: '#14532D' }}>
                  <div><strong>Pro Forma (compartida por plan de negocios y estudio econométrico):</strong></div>
                  <div style={{ marginTop: 4 }}>
                    Ingresos: {caseFinancials.revenue_trajectory.map((v) => `$${v.toLocaleString()}`).join(' → ')}
                  </div>
                  <div>
                    Acumulado 5 años: <strong>${caseFinancials.cumulative_revenue_5yr.toLocaleString()}</strong>
                    {caseFinancials.initial_capital ? ` · Capital inicial: $${caseFinancials.initial_capital.toLocaleString()}` : ''}
                    {caseFinancials.avg_revenue_per_client ? ` · Ingreso/cliente: $${caseFinancials.avg_revenue_per_client.toLocaleString()}` : ''}
                  </div>

                  {/* DE DÓNDE SALIERON ESTAS CIFRAS.
                      La Pro Forma ya se mostraba; lo que faltaba era decir si es
                      propia del cliente o el molde compartido. Un expediente con
                      cifras de plantilla se veía IGUAL que uno derivado de verdad,
                      y así se presentaron varios peticionarios de sectores
                      distintos con las mismas proyecciones (sep 2026).

                      El aviso aparece SÓLO cuando hay algo que revisar. Un cartel
                      permanente se deja de leer a la semana, y entonces no avisa
                      nada el día que importa. */}
                  {caseFinancials.procedencia && (
                    <div style={{ marginTop: 8 }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: '0.75rem', fontWeight: 600,
                        background: caseFinancials.procedencia.propia ? '#DCFCE7' : '#FEF3C7',
                        color: caseFinancials.procedencia.propia ? '#166534' : '#92400E',
                        border: `1px solid ${caseFinancials.procedencia.propia ? '#86EFAC' : '#FCD34D'}`,
                      }}>
                        {caseFinancials.procedencia.propia ? '✓ ' : '⚠ '}
                        {caseFinancials.procedencia.etiqueta}
                      </span>
                      {caseFinancials.procedencia.aviso && (
                        <div style={{ marginTop: 5, fontSize: '0.78rem', color: '#92400E', lineHeight: 1.45 }}>
                          {caseFinancials.procedencia.aviso}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ClientDashboard = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // react-query: cachea los stats del cliente para que volver a este hub sea
  // instantáneo. staleTime 0 = sirve el caché al instante y revalida en segundo
  // plano (así los contadores reflejan documentos recién creados).
  const { data, isLoading: loading } = useQuery({
    queryKey: ['clientStats', clientId],
    staleTime: 0,
    retry: false, // el manejo de error (404/401/403/fallback) es explícito abajo
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}/stats`);
        return { client: response.data.client, stats: response.data };
      } catch (error) {
        const status = error.response?.status;
        if (status === 404) {
          toast.error('Cliente no encontrado. Es posible que haya sido eliminado.');
          setTimeout(() => navigate('/dashboard'), 3000);
          throw error;
        }
        if (status === 401 || status === 403) {
          toast.error('No tienes acceso a este cliente');
          setTimeout(() => navigate('/dashboard'), 3000);
          throw error;
        }
        // Otros errores: al menos cargar datos básicos del cliente
        toast.warning('Cargando datos del cliente...');
        const basicResponse = await axios.get(`${API}/clients/${clientId}`);
        return { client: basicResponse.data, stats: null };
      }
    },
  });
  const client = data?.client || null;
  const stats = data?.stats || null;

  const handleViewDocuments = (type) => {
    // Navigate to view list of documents for this client
    navigate(`/client-documents/${clientId}/${type}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  if (!client) {
    return <div>Cliente no encontrado</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} style={{ color: '#374151', flexShrink: 0 }}>
            <ArrowLeft className="mr-2" size={18} />
            Volver
          </Button>
          <div style={{ width: '1px', height: '24px', background: '#E5E7EB', flexShrink: 0 }} />
          <div style={{ width: '32px', height: '32px', background: '#FFFBEB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={16} style={{ color: '#F8BF13' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: '700', fontSize: '1rem', color: '#111827', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</h1>
            <p style={{ fontSize: '0.75rem', color: '#6B7280', margin: 0 }}>{client.email}</p>
          </div>
          <Button onClick={() => navigate(`/trash/${clientId}`)} variant="outline" size="sm" style={{ color: '#DC2626', borderColor: '#FECACA', flexShrink: 0 }}>
            <Trash2 className="mr-1" size={14} />
            Papelera
          </Button>
        </div>
      </header>

      <main className="dashboard-main" style={{ padding: '2rem 3rem', background: '#F9FAFB', minHeight: 'calc(100vh - 80px)' }}>
        
        {/* Client Info Card */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Info size={16} style={{ color: '#F8BF13' }} />
            Información del Cliente
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
            {client.company && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Empresa</p>
                <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>{client.company}</p>
              </div>
            )}
            {client.country && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>País</p>
                <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>{client.country}</p>
              </div>
            )}
            {client.industry && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Industria</p>
                <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>{client.industry}</p>
              </div>
            )}
            {client.phone && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Teléfono</p>
                <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>{client.phone}</p>
              </div>
            )}
          </div>
          {client.notes && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #F3F4F6' }}>
              <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Notas</p>
              <p style={{ color: '#374151', fontSize: '0.9rem' }}>{client.notes}</p>
            </div>
          )}
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #F3F4F6', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div>
              <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Creado por</p>
              <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.875rem' }}>{client.created_by_name || 'N/A'}</p>
              <p style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>{client.created_at ? new Date(client.created_at).toLocaleString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
            </div>
            {client.updated_by_name && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '500', marginBottom: '0.25rem' }}>Última modificación</p>
                <p style={{ fontWeight: '600', color: '#111827', fontSize: '0.875rem' }}>{client.updated_by_name}</p>
                <p style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>{client.updated_at ? new Date(client.updated_at).toLocaleString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Case data — single source of truth reused by every deliverable */}
        <CaseDataCard clientId={clientId} />

        {/* Documents Grid */}
        <h2 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.4rem', fontWeight: '700', marginBottom: '1.5rem', color: '#111827' }}>
          Documentos de {client.name}
        </h2>

        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(224px, 1fr))',
          gap: '1rem'
        }}>
          
          {/* Proyecto Empresarial — el plan de negocios COMERCIAL.
              Va junto a "Propuestas EB-2 NIW", que es el proyecto de la visa,
              así que la descripción tiene que dejar claro cuál es cuál. */}
          <div
            onClick={() => handleViewDocuments('empresarial')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#0F766E'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#ECFDF5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Building2 size={19} style={{ color: '#0F766E' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Proyecto Empresarial</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Mercado, operativo, técnico, financiero e implementación</p>
            <span style={{ background: '#ECFDF5', color: '#064E3B', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.empresarial_count || 0} {(stats.empresarial_count || 0) === 1 ? 'creado' : 'creados'}
            </span>
          </div>

          {/* NIW Card */}
          <div 
            onClick={() => handleViewDocuments('niw')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#F8BF13'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#FFFBEB', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <FileText size={19} style={{ color: '#F8BF13' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Propuestas EB-2 NIW</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Documentos profesionales alineados con USCIS</p>
            <span style={{ background: '#FFFBEB', color: '#92400E', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {(stats.niw_count || 0) + (stats.niw_completed || 0)} creados
            </span>
          </div>

          {/* Patents Card */}
          <div 
            onClick={() => handleViewDocuments('patent')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#3B82F6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#EFF6FF', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Scale size={19} style={{ color: '#3B82F6' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Patentes USPTO</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Aplicaciones provisionales completas</p>
            <span style={{ background: '#EFF6FF', color: '#1E40AF', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {(stats.patent_count || 0) + (stats.patent_completed || 0)} creadas
            </span>
          </div>

          {/* Books Card */}
          <div 
            onClick={() => handleViewDocuments('book')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#EC4899'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#FDF2F8', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Book size={19} style={{ color: '#EC4899' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Libros Completos</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Escribe libros con capítulos estructurados</p>
            <span style={{ background: '#FDF2F8', color: '#9D174D', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {(stats.book_count || 0) + (stats.book_completed || 0)} creados
            </span>
          </div>

          {/* Econometric Studies Card */}
          <div 
            onClick={() => handleViewDocuments('study')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#8B5CF6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#F5F3FF', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <TrendingUp size={19} style={{ color: '#8B5CF6' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Estudios Econométricos</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Análisis riguroso con 16 secciones</p>
            <span style={{ background: '#F5F3FF', color: '#5B21B6', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.study_count || 0} creados
            </span>
          </div>

          {/* White Paper Card */}
          <div 
            onClick={() => handleViewDocuments('whitepaper')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#10B981'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#ECFDF5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <FileBarChart size={19} style={{ color: '#10B981' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>White Paper Técnico</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Documentos técnicos de 16 secciones profesionales</p>
            <span style={{ background: '#ECFDF5', color: '#065F46', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {(stats.whitepaper_count || 0) + (stats.whitepaper_completed || 0)} creados
            </span>
          </div>

          {/* Recommendation Letter Card */}
          <div 
            onClick={() => handleViewDocuments('recommendation')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#F97316'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#FFF7ED', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Mail size={19} style={{ color: '#F97316' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Cartas de Recomendación</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Cartas profesionales para visas EB-2 NIW y O-1</p>
            <span style={{ background: '#FFF7ED', color: '#9A3412', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.recommendation_letter_count || 0} creadas
            </span>
          </div>

          {/* Case Studies Card */}
          <div 
            onClick={() => handleViewDocuments('casestudy')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', position: 'relative' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#047857'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#ECFDF5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Briefcase size={19} style={{ color: '#047857' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Casos de Estudio Empresariales</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Análisis estilo Harvard Business School</p>
            <span style={{ background: '#ECFDF5', color: '#064E3B', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.case_study_count || 0} {(stats.case_study_count || 0) === 1 ? 'creado' : 'creados'}
            </span>
          </div>

          {/* Policy Paper Card */}
          <div 
            onClick={() => handleViewDocuments('policypaper')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#EC4899'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#FDF2F8', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Globe size={19} style={{ color: '#EC4899' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Reporte de Impacto Social</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Policy papers con impacto social</p>
            <span style={{ background: '#FDF2F8', color: '#9D174D', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.policypaper_count || 0} creados
            </span>
          </div>

          {/* Expert Letter Card */}
          <div 
            onClick={() => handleViewDocuments('expert')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#7C3AED'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#F5F3FF', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Award size={19} style={{ color: '#7C3AED' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Cartas de Expertos</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Cartas profesionales de expertos para visas</p>
            <span style={{ background: '#F5F3FF', color: '#4C1D95', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.expert_count || 0} creadas
            </span>
          </div>

          {/* Self-Petition Letter Card */}
          <div 
            onClick={() => handleViewDocuments('selfpetition')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#EA580C'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#FFF7ED', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Scale size={19} style={{ color: '#EA580C' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Cartas de Autopetición</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Cover Letters EB-2 NIW (I-140)</p>
            <span style={{ background: '#FFF7ED', color: '#9A3412', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.selfpetition_count || 0} creadas
            </span>
          </div>

          {/* Intent Letter Card */}
          <div 
            onClick={() => handleViewDocuments('intentletter')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#0369A1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#F0F9FF', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <FileText size={19} style={{ color: '#0369A1' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Cartas de Intención</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Personal Statement EB-2 NIW (Dhanasar)</p>
            <span style={{ background: '#F0F9FF', color: '#075985', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {stats.intent_letter_count || 0} creadas
            </span>
          </div>

          {/* Translations Card */}
          <div 
            onClick={() => handleViewDocuments('translation')}
            style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#0F766E'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
          >
            <div style={{ width: '38px', height: '38px', background: '#F0FDFA', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
              <Languages size={19} style={{ color: '#0F766E' }} />
            </div>
            <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem', color: '#111827' }}>Traducciones</h3>
            <p style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Traducciones profesionales ES → EN</p>
            <span style={{ background: '#F0FDFA', color: '#134E4A', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
              {(stats.translation_count || 0) + (stats.certified_translation_count || 0)} creadas
            </span>
          </div>

        </div>
      </main>
    </div>
  );
};


// Component removed - books must always be associated with a client

export default ClientDashboard;
